import { Button, Card } from '@brain/ui'
import { useQuery } from '@tanstack/react-query'
import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { meQuery } from '../lib/api.ts'
import { authClient } from '../lib/auth.ts'

/**
 * Browser half of the Mac-app login (ADR 0003): the app opens this page in the system browser,
 * where passkeys work; we hand a short-lived one-time token back via the brain:// deep link.
 */
export const Route = createFileRoute('/desktop-login')({ component: DesktopLogin })

function DesktopLogin() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const me = useQuery(meQuery)
  const [link, setLink] = useState<string | null>(null)
  const [failed, setFailed] = useState(false)

  useEffect(() => {
    if (me.isPending) return
    if (!me.data) {
      void navigate({ to: '/login', search: { redirect: '/desktop-login' } })
      return
    }
    let cancelled = false
    authClient.oneTimeToken
      .generate()
      .then(({ data, error }) => {
        if (cancelled) return
        if (error || !data?.token) return setFailed(true)
        const url = `brain://auth/callback?token=${encodeURIComponent(data.token)}`
        setLink(url)
        window.location.href = url
      })
      .catch(() => !cancelled && setFailed(true))
    return () => {
      cancelled = true
    }
  }, [me.isPending, me.data, navigate])

  return (
    <div className="flex min-h-dvh items-center justify-center bg-page px-4">
      <Card className="w-full max-w-sm p-8">
        <h1 className="text-[20px] text-text-1">{t('desktop.title')}</h1>
        {failed ? (
          <p role="alert" className="mt-3 text-[14px] text-apricot-fg">
            {t('desktop.error')}
          </p>
        ) : link ? (
          <>
            <p className="mt-3 text-[14px] text-text-2">{t('desktop.done')}</p>
            <Button asChild variant="primary" className="mt-4">
              <a href={link}>{t('desktop.open')}</a>
            </Button>
          </>
        ) : (
          <p className="mt-3 text-[14px] text-text-2">{t('desktop.working')}</p>
        )}
      </Card>
    </div>
  )
}
